/**
 * Admin category select + AI autofix allowlist.
 * Keep slugs in sync with `src/types/canonicalProduct.ts` PRODUCT_CATEGORY_SLUGS_LIST.
 * `src/__tests__/productCategoryAlignment.test.ts` fails if these lists diverge.
 */

export interface ProductCategorySelectOption {
  value: string;
  label: string;
}

export interface ProductCategorySelectGroup {
  label: string;
  options: ProductCategorySelectOption[];
}

export const PRODUCT_CATEGORY_SELECT_GROUPS: ProductCategorySelectGroup[] = [
  {
    label: 'CAMERAS',
    options: [
      { value: 'mirrorless', label: 'Mirrorless' },
      { value: 'dslr', label: 'DSLR' },
      { value: 'compact', label: 'Compact' },
      { value: 'medium-format', label: 'Medium Format' },
      { value: 'film', label: 'Film' },
      { value: 'action-camera', label: 'Action Camera' },
      { value: 'instant', label: 'Instant' },
      { value: 'video', label: 'Video / Cinema' },
    ],
  },
  {
    label: 'LENSES',
    options: [
      { value: 'lens-prime', label: 'Lens — Prime' },
      { value: 'lens-zoom', label: 'Lens — Zoom' },
      { value: 'lens-macro', label: 'Lens — Macro' },
      { value: 'lens-telephoto', label: 'Lens — Telephoto' },
      { value: 'lens-wide-angle', label: 'Lens — Wide Angle' },
      { value: 'lens-fisheye', label: 'Lens — Fisheye' },
      { value: 'lens-tilt-shift', label: 'Lens — Tilt-Shift' },
      { value: 'lens-cine', label: 'Lens — Cine' },
    ],
  },
  {
    label: 'LENS ACCESSORIES',
    options: [
      { value: 'lens-adapter', label: 'Lens Adapter' },
      { value: 'teleconverter', label: 'Teleconverter' },
      { value: 'lens-converter', label: 'Lens Converter' },
      { value: 'lens-hood', label: 'Lens Hood' },
      { value: 'lens-cap', label: 'Lens Cap' },
      { value: 'lens-filter', label: 'Lens Filter' },
    ],
  },
  {
    label: 'FLASH & LIGHTING',
    options: [
      { value: 'flash', label: 'Flash' },
      { value: 'speedlight', label: 'Speedlight' },
      { value: 'studio-strobe', label: 'Studio Strobe' },
      { value: 'led-panel', label: 'LED Panel' },
      { value: 'light', label: 'Light' },
      { value: 'light-modifier', label: 'Light Modifier' },
    ],
  },
  {
    label: 'TRIPODS & SUPPORT',
    options: [
      { value: 'tripod', label: 'Tripod' },
      { value: 'monopod', label: 'Monopod' },
      { value: 'gimbal', label: 'Gimbal' },
      { value: 'tripod-head', label: 'Tripod Head' },
      { value: 'plate', label: 'Quick Release Plate' },
    ],
  },
  {
    label: 'CAGES & RIGS',
    options: [
      { value: 'cage', label: 'Cage' },
      { value: 'shoulder-rig', label: 'Shoulder Rig' },
      { value: 'slider', label: 'Slider' },
      { value: 'mount', label: 'Mount' },
      { value: 'clamp', label: 'Clamp' },
      { value: 'rail', label: 'Rod / Rail' },
      { value: 'arm', label: 'Magic Arm' },
      { value: 'follow-focus', label: 'Follow Focus' },
      { value: 'matte-box', label: 'Matte Box' },
      { value: 'phone-rig', label: 'Phone Rig' },
    ],
  },
  {
    label: 'AUDIO',
    options: [
      { value: 'microphone', label: 'Microphone' },
      { value: 'wireless-mic', label: 'Wireless Mic System' },
      { value: 'audio-recorder', label: 'Audio Recorder' },
      { value: 'audio-accessory', label: 'Audio Accessory' },
    ],
  },
  {
    label: 'BAGS & CASES',
    options: [
      { value: 'bag', label: 'Bag' },
      { value: 'backpack', label: 'Backpack' },
      { value: 'hard-case', label: 'Hard Case' },
    ],
  },
  {
    label: 'BATTERIES & POWER',
    options: [
      { value: 'battery', label: 'Battery' },
      { value: 'charger', label: 'Charger' },
      { value: 'battery-grip', label: 'Battery Grip' },
      { value: 'power-adapter', label: 'Power Adapter' },
    ],
  },
  {
    label: 'MEMORY & STORAGE',
    options: [
      { value: 'memory-card', label: 'Memory Card' },
      { value: 'card-reader', label: 'Card Reader' },
    ],
  },
  {
    label: 'CABLES & CONNECTIVITY',
    options: [
      { value: 'cable', label: 'Cable' },
      { value: 'wireless-transmitter', label: 'Wireless Transmitter' },
    ],
  },
  {
    label: 'DISPLAY & MONITORING',
    options: [
      { value: 'monitor', label: 'Monitor' },
      { value: 'external-recorder', label: 'External Recorder' },
    ],
  },
  {
    label: 'REMOTE & CONTROL',
    options: [
      { value: 'remote', label: 'Remote' },
      { value: 'grip', label: 'Grip' },
    ],
  },
  {
    label: 'RENTAL',
    options: [{ value: 'rental', label: 'Rental Kit' }],
  },
  {
    label: 'FILM & DARKROOM',
    options: [
      { value: 'film-stock', label: 'Film Stock' },
      { value: 'darkroom', label: 'Darkroom & Chemistry' },
    ],
  },
  {
    label: 'OTHER',
    options: [
      { value: 'strap', label: 'Strap' },
      { value: 'filter', label: 'Filter' },
      { value: 'drone', label: 'Drone' },
      { value: 'cleaning', label: 'Cleaning' },
      { value: 'screen-protector', label: 'Screen Protector' },
      { value: 'eyecup', label: 'Eyecup' },
      { value: 'software', label: 'Software' },
      { value: 'binocular', label: 'Binoculars' },
      { value: 'rangefinder', label: 'Rangefinder' },
      { value: 'scanner', label: 'Scanner' },
      { value: 'weather-cover', label: 'Rain / Weather Cover' },
      { value: 'spare-part', label: 'Spare Part' },
      { value: 'other', label: 'Other' },
    ],
  },
];

export const PRODUCT_CATEGORY_SLUGS_LIST: readonly string[] =
  PRODUCT_CATEGORY_SELECT_GROUPS.flatMap((group) => group.options.map((opt) => opt.value));

export const PRODUCT_CATEGORY_SLUGS = new Set<string>(PRODUCT_CATEGORY_SLUGS_LIST);
